import { Link } from 'react-router-dom';
import type { Order } from '../api/orders';
import { StatusBadge } from './StatusBadge';
import { OrderWorkflowStepper } from './OrderWorkflowStepper';
import { formatDateTime } from '../lib/format';

const PAYMENT_LABEL: Record<Order['paymentStatus'], string> = {
  pending: 'Paiement en attente',
  paid: 'Payé',
  failed: 'Paiement refusé',
  refunded: 'Remboursé'
};

const PAYMENT_CLASS: Record<Order['paymentStatus'], string> = {
  pending: 'bg-amber-50 text-amber-900 ring-amber-200/80',
  paid: 'bg-emerald-50 text-emerald-900 ring-emerald-200/80',
  failed: 'bg-red-50 text-red-800 ring-red-200/80',
  refunded: 'bg-violet-50 text-violet-900 ring-violet-200/80'
};

const vehicleFr = (v?: string) => {
  if (!v) return 'Véhicule';
  const map: Record<string, string> = {
    bike: 'Vélo',
    motorbike: 'Deux-roues',
    car: 'Voiture',
    van: 'Camionnette',
    small_truck: 'Petit camion',
    medium_truck: 'Camion',
    large_truck: 'Porteur',
    refrigerated: 'Frigorifique',
    other: 'Autre'
  };
  return map[v] ?? v;
};

type Props = {
  order: Order;
};

export const OrderListCard = ({ order }: Props) => {
  const d = order.Deliverer;

  return (
    <Link
      to={`/orders/${order.id}`}
      className="group card block overflow-hidden p-0 transition-all hover:border-brand-200/80 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
    >
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50/90 via-white to-brand-50/30 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Suivi</p>
            <p className="font-mono text-sm font-semibold text-slate-900 group-hover:text-brand-700">{order.trackingCode}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${PAYMENT_CLASS[order.paymentStatus]}`}
            >
              {PAYMENT_LABEL[order.paymentStatus]}
            </span>
            <StatusBadge status={order.status} />
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <p className="text-xs text-slate-500">Montant estimé</p>
            <p className="text-2xl font-bold tabular-nums tracking-tight text-slate-900">
              {order.price.toFixed(2)}{' '}
              <span className="text-base font-semibold text-slate-600">{order.currency}</span>
            </p>
            {order.distance > 0 && (
              <p className="mt-0.5 text-xs text-slate-500">Distance indicative · {order.distance.toFixed(1)} km</p>
            )}
          </div>
          <span className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition group-hover:bg-brand-600">
            Voir le détail
            <svg className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm ring-1 ring-slate-200/80" aria-hidden>
                ↑
              </span>
              Collecte
            </p>
            <p className="mt-1.5 text-sm font-medium leading-snug text-slate-800">{order.pickupAddress}</p>
          </div>
          <div className="rounded-lg border border-emerald-100/80 bg-emerald-50/40 p-3">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800/90">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-200/80" aria-hidden>
                ↓
              </span>
              Livraison
            </p>
            <p className="mt-1.5 text-sm font-medium leading-snug text-slate-800">{order.deliveryAddress}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Destinataire</p>
            <p className="font-medium text-slate-900">{order.recipientName}</p>
            {order.recipientPhone && <p className="text-xs text-slate-500">{order.recipientPhone}</p>}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Expéditeur</p>
            <p className="font-medium text-slate-900">{order.senderName}</p>
            {order.senderPhone && <p className="text-xs text-slate-500">{order.senderPhone}</p>}
          </div>
        </div>

        {d && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Livreur</span>
            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{d.id.slice(0, 8)}…</span>
            {d.companyName && <span className="text-slate-700">{d.companyName}</span>}
            <span className="text-slate-500">· {vehicleFr(d.vehicleType)}</span>
            {typeof d.rating === 'number' && d.rating > 0 && (
              <span className="ml-auto text-amber-700">★ {d.rating.toFixed(1)}</span>
            )}
          </div>
        )}

        <OrderWorkflowStepper status={order.status} />

        <p className="text-right text-[11px] text-slate-400">Créée le {formatDateTime(order.createdAt)}</p>
      </div>
    </Link>
  );
};
